// Dashboard CRUD client. Mirrors middleware/routes/dashboards.py.

import type { ChartSpec } from "../features/charts/types";
import { api } from "./base";

export interface DashboardSummary {
  id: number;
  name: string;
  organization: string;
  insight_count: number;
  is_default: boolean;
  shared_with_org: boolean;
  created_at: string | null;
}

export interface InsightView {
  id: number;
  name: string | null;
  user_query: string;
  chart_spec: ChartSpec;
  chart_type: string | null;
  insight_order: number | null;
  created_at: string | null;
}

export interface DashboardDetail {
  id: number;
  name: string;
  organization: string;
  is_default: boolean;
  shared_with_org: boolean;
  insights: InsightView[];
}

async function jsonFetch<T>(bearer: string, url: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(url, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${bearer}`,
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
    },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`${init?.method ?? "GET"} ${url} → ${resp.status}: ${text}`);
  }
  return (await resp.json()) as T;
}

export function fetchDashboards(bearer: string, organization?: string): Promise<DashboardSummary[]> {
  const qs = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  return jsonFetch<DashboardSummary[]>(bearer, `${api("/dashboards")}${qs}`);
}

export function createDashboard(bearer: string, name: string, organization: string): Promise<DashboardSummary> {
  return jsonFetch(bearer, api("/dashboards"), { method: "POST", body: JSON.stringify({ name, organization }) });
}

export function renameDashboard(bearer: string, id: number, name: string): Promise<DashboardSummary> {
  return jsonFetch(bearer, `${api("/dashboards")}/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
}

export async function deleteDashboard(bearer: string, id: number): Promise<void> {
  await jsonFetch<{ ok: true }>(bearer, `${api("/dashboards")}/${id}`, { method: "DELETE" });
}

export function getDashboard(bearer: string, id: number): Promise<DashboardDetail> {
  return jsonFetch<DashboardDetail>(bearer, `${api("/dashboards")}/${id}`);
}

export interface AddInsightArgs {
  chart_spec: ChartSpec;
  user_query: string;
  datasource_id: string;
  insight_name?: string;
}

export function addInsight(bearer: string, dashboardId: number, args: AddInsightArgs): Promise<InsightView> {
  return jsonFetch<InsightView>(bearer, `${api("/dashboards")}/${dashboardId}/insights`, {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export async function removeInsight(bearer: string, dashboardId: number, insightId: number): Promise<void> {
  await jsonFetch<{ ok: true }>(bearer, `${api("/dashboards")}/${dashboardId}/insights/${insightId}`, { method: "DELETE" });
}

export async function reorderInsights(bearer: string, dashboardId: number, insightIds: number[]): Promise<void> {
  await jsonFetch<{ ok: true }>(bearer, `${api("/dashboards")}/${dashboardId}/reorder`, {
    method: "POST",
    body: JSON.stringify({ insight_ids: insightIds }),
  });
}

export function refreshInsight(bearer: string, dashboardId: number, insightId: number): Promise<InsightView> {
  return jsonFetch<InsightView>(bearer, `${api("/dashboards")}/${dashboardId}/insights/${insightId}/refresh`, {
    method: "POST",
  });
}

export interface OrgShareResult {
  ok: true;
  shared_with_org: boolean;
  url: string;
}

export function toggleOrgShare(
  bearer: string,
  dashboardId: number,
  shared: boolean,
): Promise<OrgShareResult> {
  return jsonFetch<OrgShareResult>(bearer, `${api("/dashboards")}/${dashboardId}/share`, {
    method: "POST",
    body: JSON.stringify({ shared }),
  });
}
